import type { MedplumClient } from "@medplum/core";
import type { Basic } from "@medplum/fhirtypes";
import {
  FhirResource,
  type SearchOptions,
  type SubscriptionCallback,
} from "@vibrant/models/fhir/FhirResource";
import type { ResourceData, ResourceRef } from "@vibrant/models/fhir/fhir-resource-types";
import { cleanObject } from "@vibrant/models/fhir/fhir-resource-utils";
import { paginatedSearch } from "@/lib/fhir/bundle";
import { getIdFromReferenceString, pruneSystemValuePairs } from "@/lib/medplum/utils";
import { chunkArray } from "@/lib/utils/chunk-array";

/** Patient ids per search. More ids can push a joined query past the request-line limit. */
const MAX_PATIENT_IDS_PER_QUERY = 100;

interface MemberTagIdsParams {
  patient: ResourceRef;
  practice: ResourceRef;
  tagIds: string[];
}

/**
 * MemberTagAssignment (FHIR: Basic)
 *
 * Represents an assignment of a tag to a member. This junction resource
 * enables efficient queries in both directions:
 * - "Which tags does member X have?" → search by subject (member)
 * - "Which members have tag Y?" → search by identifier (tag reference)
 *
 * Each assignment is unique per member-tag combination. The same tag cannot
 * be assigned to the same member twice.
 *
 * Shape:
 * - Basic.code.coding: { system: "vibrant.resource-type", code: "MemberTagAssignment" }
 * - Basic.subject: Reference to Patient
 * - Basic.identifier: [
 *     { system: "vibrant.member-tag-assignment.tag", value: <tag-id> },
 *     { system: "vibrant.member-tag-assignment.practice", value: <practice-id> }
 *   ]
 */
export class MemberTagAssignment extends FhirResource<Basic> {
  static readonly ResourceType = "Basic" as const;

  static readonly Code = {
    System: "vibrant.resource-type",
    Code: "MemberTagAssignment",
    Display: "Member Tag Assignment",
  } as const;

  static readonly Identifier = {
    /** The ID of the assigned tag (Basic resource ID). */
    TagId: "vibrant.member-tag-assignment.tag",
    /** The practice ID for efficient practice-scoped queries. */
    PracticeId: "vibrant.member-tag-assignment.practice",
  } as const;

  static readonly Sort = {
    CreatedDate: "_lastUpdated",
  } as const;

  //------------------------------------------------------------------------------------------------
  //                                     VIRTUAL PROPERTIES (GETTERS)
  //------------------------------------------------------------------------------------------------

  /**
   * Reference to the member that has this tag assignment.
   */
  get patientRef(): ResourceRef | undefined {
    return this.resource.subject?.reference
      ? { reference: this.resource.subject.reference }
      : undefined;
  }

  /**
   * The member ID extracted from the member reference.
   */
  get patientId(): string | undefined {
    return this.patientRef?.reference?.split("/")[1];
  }

  /**
   * The ID of the assigned tag.
   */
  get tagId(): string | undefined {
    const value = this.getIdentifier(MemberTagAssignment.Identifier.TagId);
    return typeof value === "string" ? value : undefined;
  }

  /**
   * Reference to the tag (Basic resource).
   */
  get tagRef(): ResourceRef | undefined {
    const tagId = this.tagId;
    return tagId ? { reference: `Basic/${tagId}` } : undefined;
  }

  /**
   * The practice ID for this assignment.
   */
  get practiceId(): string | undefined {
    const value = this.getIdentifier(MemberTagAssignment.Identifier.PracticeId);
    return typeof value === "string" ? value : undefined;
  }

  /**
   * Reference to the practice (Organization).
   */
  get practiceRef(): ResourceRef | undefined {
    const practiceId = this.practiceId;
    return practiceId ? { reference: `Organization/${practiceId}` } : undefined;
  }

  //------------------------------------------------------------------------------------------------
  //                                     CLASS METHODS (STATIC)
  //------------------------------------------------------------------------------------------------

  /**
   * Converts MemberTagAssignmentData to a FHIR Basic resource object.
   *
   * @param data - The member tag assignment data.
   * @returns The FHIR Basic resource object.
   */
  static toFhirObject(data: MemberTagAssignmentData): ResourceData<Basic> {
    const practiceId = getIdFromReferenceString(data.practice.reference, "Organization");

    return {
      code: {
        coding: [
          {
            system: MemberTagAssignment.Code.System,
            code: MemberTagAssignment.Code.Code,
            display: MemberTagAssignment.Code.Display,
          },
        ],
      },
      subject: data.patient,
      identifier: pruneSystemValuePairs([
        { system: MemberTagAssignment.Identifier.TagId, value: data.tagId },
        { system: MemberTagAssignment.Identifier.PracticeId, value: practiceId },
      ]),
    };
  }

  /**
   * Converts a MemberTagAssignmentQuery to a FHIR search query.
   *
   * @param query - The member tag assignment query.
   * @returns The FHIR search query.
   */
  static toFhirQuery(query: MemberTagAssignmentQuery) {
    const identifierFilters: string[] = [];
    const filterParts: string[] = [];

    if (query.tagId) {
      const tagToken = `${MemberTagAssignment.Identifier.TagId}|${query.tagId}`;
      identifierFilters.push(tagToken);
      filterParts.push(`identifier eq "${tagToken}"`);
    }
    if (query.practice) {
      const practiceId = getIdFromReferenceString(query.practice.reference, "Organization");
      if (practiceId) {
        const practiceToken = `${MemberTagAssignment.Identifier.PracticeId}|${practiceId}`;
        identifierFilters.push(practiceToken);
        filterParts.push(`identifier eq "${practiceToken}"`);
      }
    }

    const shouldUseFilter = identifierFilters.length > 1;

    return {
      // `system|code`, never a bare code - see MemberTag.toFhirQuery.
      code: `${MemberTagAssignment.Code.System}|${MemberTagAssignment.Code.Code}`,
      subject: query.patient?.reference,
      identifier: shouldUseFilter ? undefined : identifierFilters[0],
      _filter: shouldUseFilter ? filterParts.join(" and ") : undefined,
    };
  }

  /**
   * Creates a new member tag assignment, or returns the existing one if already assigned.
   * This operation is idempotent — calling it multiple times with the same data has no additional effect.
   *
   * @param medplum - The Medplum client.
   * @param data - The member tag assignment data.
   * @returns The member tag assignment (newly created or existing).
   */
  static async create(
    medplum: MedplumClient,
    data: MemberTagAssignmentData,
  ): Promise<MemberTagAssignment> {
    // Return existing assignment if already present (idempotent)
    const existing = await MemberTagAssignment.getByPatientAndTag(
      medplum,
      data.patient,
      data.tagId,
    );
    if (existing) {
      return existing;
    }

    // The practice has to be passed explicitly. The create path's self-heal derives an account
    // only from a Patient's own managingOrganization; for every other type it deliberately
    // refuses to read the referenced Patient, so a Patient `subject` inherits nothing. Without
    // this the row lands in the Project and Patient compartments only - never the practice's -
    // which puts it out of reach of every compartment-pinned read (the v2 serving AccessPolicy
    // included).
    const resource = await MemberTagAssignment.createResource(
      medplum,
      MemberTagAssignment.toFhirObject(data),
      { practiceOrganization: data.practice },
    );
    return new MemberTagAssignment(resource);
  }

  /**
   * Gets a member tag assignment by ID.
   *
   * @param medplum - The Medplum client.
   * @param id - The ID of the member tag assignment.
   * @returns The member tag assignment or undefined if not found.
   */
  static async getById(
    medplum: MedplumClient,
    id: string,
  ): Promise<MemberTagAssignment | undefined> {
    try {
      const resource = await medplum.readResource(MemberTagAssignment.ResourceType, id);
      return resource ? new MemberTagAssignment(resource) : undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Gets a specific member-tag assignment.
   *
   * @param medplum - The Medplum client.
   * @param patient - The member reference.
   * @param tagId - The tag ID.
   * @returns The member tag assignment or undefined if not found.
   */
  static async getByPatientAndTag(
    medplum: MedplumClient,
    patient: ResourceRef,
    tagId: string,
  ): Promise<MemberTagAssignment | undefined> {
    const resource = await MemberTagAssignment.searchOneResource(
      medplum,
      MemberTagAssignment.toFhirQuery({ patient, tagId }),
    );
    return resource ? new MemberTagAssignment(resource) : undefined;
  }

  /**
   * Searches for member tag assignments.
   *
   * @param medplum - The Medplum client.
   * @param query - The search query.
   * @param options - Search options (pagination, sorting).
   * @returns Array of member tag assignments.
   */
  static async search(
    medplum: MedplumClient,
    query: MemberTagAssignmentQuery,
    options?: SearchOptions,
  ): Promise<MemberTagAssignment[]> {
    const resources = await MemberTagAssignment.searchResources(
      medplum,
      MemberTagAssignment.toFhirQuery(query),
      options,
    );
    return resources.map((resource) => new MemberTagAssignment(resource));
  }

  /**
   * Gets all tag assignments for a member.
   * Handles pagination to fetch all assignments, not just the default 20.
   *
   * @param medplum - The Medplum client.
   * @param patient - The member reference.
   * @param options - Optional search options. If limit is provided, pagination respects it.
   *                  If no limit is provided, all assignments are fetched.
   * @returns Array of member tag assignments.
   */
  static async getByPatient(
    medplum: MedplumClient,
    patient: ResourceRef,
    options?: SearchOptions,
  ): Promise<MemberTagAssignment[]> {
    // If a limit is explicitly provided, use it (for pagination scenarios)
    // Otherwise, fetch all assignments by paginating through results
    if (options?.limit !== undefined) {
      return MemberTagAssignment.search(medplum, { patient }, options);
    }

    // Fetch all assignments with pagination (default limit is 20, so we need to paginate)
    const PAGE_SIZE = 1000;
    const allAssignments: MemberTagAssignment[] = [];
    let offset = 0;
    let batch: MemberTagAssignment[];

    do {
      batch = await MemberTagAssignment.search(medplum, { patient }, { limit: PAGE_SIZE, offset });
      allAssignments.push(...batch);
      offset += PAGE_SIZE;
    } while (batch.length === PAGE_SIZE);

    return allAssignments;
  }

  /**
   * Gets all member assignments for a tag.
   *
   * @param medplum - The Medplum client.
   * @param tagId - The tag ID.
   * @param options - Search options (pagination).
   * @returns Array of member tag assignments.
   */
  static async getByTag(
    medplum: MedplumClient,
    tagId: string,
    options?: SearchOptions,
  ): Promise<MemberTagAssignment[]> {
    return MemberTagAssignment.search(medplum, { tagId }, options);
  }

  /**
   * Gets all tag assignments for a practice.
   *
   * @param medplum - The Medplum client.
   * @param practice - The practice reference.
   * @param options - Search options (pagination).
   * @returns Array of member tag assignments.
   */
  static async getByPractice(
    medplum: MedplumClient,
    practice: ResourceRef,
    options?: SearchOptions,
  ): Promise<MemberTagAssignment[]> {
    return MemberTagAssignment.search(medplum, { practice }, options);
  }

  /**
   * Deletes a member tag assignment by ID.
   *
   * @param medplum - The Medplum client.
   * @param id - The ID of the member tag assignment to delete.
   */
  static async delete(medplum: MedplumClient, id: string): Promise<void> {
    await medplum.deleteResource(MemberTagAssignment.ResourceType, id);
  }

  /**
   * Removes a tag from a member.
   *
   * @param medplum - The Medplum client.
   * @param patient - The member reference.
   * @param tagId - The tag ID to remove.
   * @returns True if the assignment was found and deleted, false otherwise.
   */
  static async removeTagFromPatient(
    medplum: MedplumClient,
    patient: ResourceRef,
    tagId: string,
  ): Promise<boolean> {
    const assignment = await MemberTagAssignment.getByPatientAndTag(medplum, patient, tagId);
    if (!assignment) {
      return false;
    }
    await MemberTagAssignment.delete(medplum, assignment.id);
    return true;
  }

  /**
   * Removes all assignments for a specific tag (useful when deleting a tag).
   * Handles pagination to ensure all assignments are deleted regardless of count.
   *
   * @param medplum - The Medplum client.
   * @param tagId - The tag ID.
   * @returns The number of assignments deleted.
   */
  static async removeAllAssignmentsForTag(medplum: MedplumClient, tagId: string): Promise<number> {
    const PAGE_SIZE = 1000;
    let totalDeleted = 0;
    let assignments: MemberTagAssignment[];

    // Loop until we get fewer results than the page size (indicating final page)
    do {
      assignments = await MemberTagAssignment.getByTag(medplum, tagId, { limit: PAGE_SIZE });
      await Promise.all(
        assignments.map((assignment) => MemberTagAssignment.delete(medplum, assignment.id)),
      );
      totalDeleted += assignments.length;
    } while (assignments.length === PAGE_SIZE);

    return totalDeleted;
  }

  /**
   * Removes all tag assignments for a member.
   * Handles pagination to ensure all assignments are deleted regardless of count.
   *
   * @param medplum - The Medplum client.
   * @param patient - The member reference.
   * @returns The number of assignments deleted.
   */
  static async removeAllAssignmentsForPatient(
    medplum: MedplumClient,
    patient: ResourceRef,
  ): Promise<number> {
    const PAGE_SIZE = 1000;
    let totalDeleted = 0;
    let assignments: MemberTagAssignment[];

    // Loop until we get fewer results than the page size (indicating final page)
    do {
      assignments = await MemberTagAssignment.search(medplum, { patient }, { limit: PAGE_SIZE });
      await Promise.all(
        assignments.map((assignment) => MemberTagAssignment.delete(medplum, assignment.id)),
      );
      totalDeleted += assignments.length;
    } while (assignments.length === PAGE_SIZE);

    return totalDeleted;
  }

  /**
   * Bulk assigns multiple tags to a member.
   * This operation is idempotent — existing assignments are returned as-is.
   *
   * @param medplum - The Medplum client.
   * @param patient - The member reference.
   * @param practice - The practice reference.
   * @param tagIds - Array of tag IDs to assign.
   * @returns Array of assignments (newly created or existing).
   */
  static async bulkAssignTags(
    medplum: MedplumClient,
    { patient, practice, tagIds }: MemberTagIdsParams,
  ): Promise<MemberTagAssignment[]> {
    const assignments = await Promise.all(
      tagIds.map((tagId) => MemberTagAssignment.create(medplum, { patient, practice, tagId })),
    );
    return assignments;
  }

  /**
   * Fetches all tag assignments for a member, handling pagination.
   * @deprecated Use getByPatient() instead, which now handles pagination automatically.
   * This method is kept for internal use by syncMemberTags to avoid breaking changes.
   *
   * @param medplum - The Medplum client.
   * @param patient - The member reference.
   * @returns Array of all member tag assignments.
   */
  private static async getAllByPatient(
    medplum: MedplumClient,
    patient: ResourceRef,
  ): Promise<MemberTagAssignment[]> {
    // Delegate to getByPatient which now handles pagination
    return MemberTagAssignment.getByPatient(medplum, patient);
  }

  /**
   * Returns the assignments that tie these patients to any of these tags, in one paged search per
   * 100 patients. Use it to find tag-locked patients for a batch, instead of one `getByPatient`
   * each. It reads every page: a missing match would read as an unlocked patient.
   */
  static async searchForPatientsWithTags(
    medplum: MedplumClient,
    { patientIds, tagIds }: { patientIds: string[]; tagIds: string[] },
  ): Promise<MemberTagAssignment[]> {
    if (!patientIds.length || !tagIds.length) return [];

    // a comma inside one param is an OR: any of these tags, any of these patients
    const identifier = tagIds
      .map((tagId) => `${MemberTagAssignment.Identifier.TagId}|${tagId}`)
      .join(",");
    const batches = await Promise.all(
      chunkArray(patientIds, MAX_PATIENT_IDS_PER_QUERY).map(async (chunk) => {
        const query = cleanObject({
          ...MemberTagAssignment.toFhirQuery({}),
          identifier,
          subject: chunk.map((patientId) => `Patient/${patientId}`).join(","),
          _count: 1000,
        });
        const assignments: MemberTagAssignment[] = [];
        for await (const resource of paginatedSearch<Basic>({
          medplum,
          resourceType: MemberTagAssignment.ResourceType,
          query,
        })) {
          assignments.push(new MemberTagAssignment(resource));
        }
        return assignments;
      }),
    );
    return batches.flat();
  }

  /**
   * Fetches all tag assignments for a tag, handling pagination.
   *
   * @param medplum - The Medplum client.
   * @param tagId - The tag ID.
   * @returns Array of all member tag assignments for the tag.
   */
  static async getAllByTag(medplum: MedplumClient, tagId: string): Promise<MemberTagAssignment[]> {
    const PAGE_SIZE = 1000;
    const allAssignments: MemberTagAssignment[] = [];
    let offset = 0;
    let batch: MemberTagAssignment[];

    do {
      batch = await MemberTagAssignment.search(medplum, { tagId }, { limit: PAGE_SIZE, offset });
      allAssignments.push(...batch);
      offset += PAGE_SIZE;
    } while (batch.length === PAGE_SIZE);

    return allAssignments;
  }

  /**
   * Syncs a member's tags to match the provided tag IDs within a specific practice.
   * Adds missing tags and removes tags not in the list.
   * Only affects tags from the specified practice; tags from other practices are preserved.
   * Handles pagination to ensure all existing assignments are considered.
   *
   * @param medplum - The Medplum client.
   * @param patient - The member reference.
   * @param practice - The practice reference (only tags from this practice are synced).
   * @param tagIds - The desired tag IDs for the member within this practice.
   * @returns Object with added and removed assignment counts.
   */
  static async syncMemberTags(
    medplum: MedplumClient,
    { patient, practice, tagIds }: MemberTagIdsParams,
  ): Promise<{ added: number; removed: number }> {
    const practiceId = getIdFromReferenceString(practice.reference, "Organization");
    const allAssignments = await MemberTagAssignment.getAllByPatient(medplum, patient);

    // Filter to only assignments from this practice to avoid affecting other practices' tags
    const currentAssignments = allAssignments.filter((a) => a.practiceId === practiceId);
    const currentTagIds = new Set(currentAssignments.map((a) => a.tagId).filter(Boolean));
    const desiredTagIds = new Set(tagIds);

    // Find tags to add (spread Set to avoid duplicate tag IDs in input)
    const tagsToAdd = [...desiredTagIds].filter((id) => !currentTagIds.has(id));

    // Find assignments to remove (already scoped to this practice via currentAssignments)
    const assignmentsToRemove = currentAssignments.filter(
      (a) => a.tagId && !desiredTagIds.has(a.tagId),
    );

    // Add new tags
    const added = await MemberTagAssignment.bulkAssignTags(medplum, {
      patient,
      practice,
      tagIds: tagsToAdd,
    });

    // Remove old tags
    await Promise.all(
      assignmentsToRemove.map((assignment) => MemberTagAssignment.delete(medplum, assignment.id)),
    );

    return {
      added: added.length,
      removed: assignmentsToRemove.length,
    };
  }

  /**
   * Subscribes to changes in member tag assignments.
   *
   * @param medplum - The Medplum client.
   * @param query - The query to filter assignments.
   * @param callback - The callback function.
   * @returns The subscription.
   */
  static subscribe(
    medplum: MedplumClient,
    query: MemberTagAssignmentQuery,
    callback: SubscriptionCallback<MemberTagAssignment>,
  ) {
    return MemberTagAssignment.subscribeToResource(
      medplum,
      MemberTagAssignment.toFhirQuery(query),
      (resource, metadata) => callback(new MemberTagAssignment(resource), metadata),
    );
  }
}

//--------------------------------------------------------------------------------------------------
//                                           TYPES
//--------------------------------------------------------------------------------------------------

/**
 * Data required to create a new member tag assignment.
 */
export interface MemberTagAssignmentData {
  /** Reference to the member to assign the tag to. */
  patient: ResourceRef;
  /** Reference to the practice for scoping. */
  practice: ResourceRef;
  /** The ID of the tag to assign (Basic resource ID). */
  tagId: string;
}

/**
 * Query parameters for searching member tag assignments.
 */
export interface MemberTagAssignmentQuery {
  /** Filter by member. */
  patient?: ResourceRef;
  /** Filter by tag ID. */
  tagId?: string;
  /** Filter by practice. */
  practice?: ResourceRef;
}
